/**
 * Global Audio Singleton
 *
 * This module provides a persistent audio element that lives outside of React's
 * render cycle. This is necessary to satisfy browser autoplay policies which
 * require play() to be called synchronously within a user gesture (click/tap).
 *
 * If we rely on React state updates to mount/unmount audio elements, the async
 * nature of rendering breaks the "user gesture" chain and browsers block playback.
 */

type AudioEventCallback = (event: Event) => void

interface AudioEventListeners {
	play?: AudioEventCallback
	pause?: AudioEventCallback
	ended?: AudioEventCallback
	error?: AudioEventCallback
	timeupdate?: AudioEventCallback
	loadedmetadata?: AudioEventCallback
	canplay?: AudioEventCallback
}

class GlobalAudioService {
	private audio: HTMLAudioElement | null = null
	private listeners: AudioEventListeners = {}

	/**
	 * Get or create the singleton audio element
	 */
	getAudioElement(): HTMLAudioElement {
		if (!this.audio) {
			this.audio = new Audio()
			this.audio.preload = 'auto'
		}
		return this.audio
	}

	/**
	 * Play audio from a source URL.
	 * This method is designed to be called synchronously from a click handler.
	 */
	play(src: string): Promise<void> {
		const audio = this.getAudioElement()

		// If the src is different, update it
		if (audio.src !== src) {
			audio.src = src
		}

		// Call play() synchronously within the user gesture context
		return audio.play().catch((error) => {
			// Log but don't throw - some browsers may still block
			if (import.meta.env.DEV) {
				console.warn('[globalAudio] play() failed:', error.name, error.message)
			}
			throw error
		})
	}

	/**
	 * Pause the current audio
	 */
	pause(): void {
		if (this.audio) {
			this.audio.pause()
		}
	}

	/**
	 * Stop and reset the audio
	 */
	stop(): void {
		if (this.audio) {
			this.audio.pause()
			this.audio.currentTime = 0
		}
	}

	/**
	 * Get the current source URL
	 */
	getCurrentSrc(): string | null {
		return this.audio?.src || null
	}

	/**
	 * Check if audio is currently playing
	 */
	isPlaying(): boolean {
		return this.audio ? !this.audio.paused : false
	}

	/**
	 * Get the audio element for direct access (e.g., for UI sync)
	 */
	getElement(): HTMLAudioElement | null {
		return this.audio
	}

	/**
	 * Set event listeners on the audio element
	 */
	setEventListeners(listeners: AudioEventListeners): void {
		const audio = this.getAudioElement()

		// Remove old listeners
		this.removeEventListeners()

		// Store and attach new listeners
		this.listeners = listeners

		if (listeners.play) audio.addEventListener('play', listeners.play)
		if (listeners.pause) audio.addEventListener('pause', listeners.pause)
		if (listeners.ended) audio.addEventListener('ended', listeners.ended)
		if (listeners.error) audio.addEventListener('error', listeners.error)
		if (listeners.timeupdate)
			audio.addEventListener('timeupdate', listeners.timeupdate)
		if (listeners.loadedmetadata)
			audio.addEventListener('loadedmetadata', listeners.loadedmetadata)
		if (listeners.canplay) audio.addEventListener('canplay', listeners.canplay)
	}

	/**
	 * Remove all event listeners
	 */
	removeEventListeners(): void {
		if (!this.audio) return

		const audio = this.audio
		const listeners = this.listeners

		if (listeners.play) audio.removeEventListener('play', listeners.play)
		if (listeners.pause) audio.removeEventListener('pause', listeners.pause)
		if (listeners.ended) audio.removeEventListener('ended', listeners.ended)
		if (listeners.error) audio.removeEventListener('error', listeners.error)
		if (listeners.timeupdate)
			audio.removeEventListener('timeupdate', listeners.timeupdate)
		if (listeners.loadedmetadata)
			audio.removeEventListener('loadedmetadata', listeners.loadedmetadata)
		if (listeners.canplay)
			audio.removeEventListener('canplay', listeners.canplay)

		this.listeners = {}
	}
}

// Export singleton instance
export const globalAudio = new GlobalAudioService()
